import React, { FC, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isEmpty, isUndefined, noop } from 'lodash'
import { MuiEvent } from '@microservices/wiskey-react-table-component'
import {
  SearchAssistant,
  SearchAssistantConsumer,
  SearchAssistantRef,
} from '@widgets/SearchAssistant'
import { SearchAssistantViewSelector } from '@widgets/SearchAssistant/ui/SearchAssistantViewSelector/SearchAssistantViewSelector'

import {
  ConfiguredEntityInitialDataWrapper,
  ConfiguredEntityProps,
  GetConfiguredEntityParamsHandle,
} from '@pages/ConfiguredEntity'
import { transformFiltersToValuesPOST } from '@pages/ConfiguredEntity/components/SearchAssistant/helpers'
import { useRefreshObjectDataByTimeWindowChange } from '@pages/ConfiguredEntity/hooks'
import { OpenFormProps } from '@pages/ConfiguredEntity/types'

import { ETimeUnit, TimeWindowInterval, useTimeWindowInterval } from '@hooks'
import { getLocalStorageContext, isNotEmpty } from '@helpers'
import { CLICK_EVENT_TYPE, CriteriaType, ENTITY, SEARCH_FIELD_TYPE } from '@constants'
import { EntityType, POSTObjectDataFilter } from '@types'

type SearchAssistantPageProps = {
  isSearchAssistantDialogWindow?: boolean
  isViewDialogWindow?: boolean
  entityId?: number
  entityCode?: string
  dialogId?: string
  onOpenForm: (
    formProps: OpenFormProps,
    event: MouseEvent<HTMLButtonElement> | MuiEvent<MouseEvent>,
    type: CLICK_EVENT_TYPE
  ) => void
  path: string
  title: string
  type: ENTITY
  windowHeight?: number
  setSearchFilter?: (body: POSTObjectDataFilter) => void
  searchFilterBody?: POSTObjectDataFilter
  entityMinHeight?: number | 'string'
  hideSA?: boolean
} & Omit<ConfiguredEntityProps, 'entity' | 'entityId' | 'entityCode' | 'onSetEntity'>

type SelectedEntity = Pick<EntityType, 'code' | 'id'> | null

export const ConfiguredEntityWithSearchAssistant: FC<SearchAssistantPageProps> = ({
  dialogId,
  onOpenForm: handleOpenForm,
  path,
  title,
  type,
  windowHeight,
  isSearchAssistantDialogWindow,
  isViewDialogWindow,
  setSearchFilter,
  searchFilterBody,
  entityMinHeight,
  hideSA = false,
  ...props
}) => {
  const [entity, setEntity] = useState<EntityType | null>(null)
  const [selectedEntity, setSelectedEntityInSA] = useState<SelectedEntity>(null)

  const ref = useRef<GetConfiguredEntityParamsHandle>(null)

  const handleSelectEntity = useCallback((entity: SelectedEntity) => {
    setSelectedEntityInSA(entity)
  }, [])

  const handleSetEntity = useCallback((value: EntityType | null) => setEntity(value), [])

  const { checkIsValidTimeWindowInterval, updateTimeWindow, actualTimeWindow } =
    useTimeWindowInterval()

  // Сохраняем последние параметры временного окна для возможности перезапуска при изменении сортировки
  const lastTimeWindowParamsRef = useRef<{
    timeWindow: TimeWindowInterval
    timeWindowField: string
    timeIncrement: number
    timeUnit: ETimeUnit
  } | null>(null)

  useRefreshObjectDataByTimeWindowChange(ref, actualTimeWindow)

  const updateSearchFilter = (body: POSTObjectDataFilter) => {
    if (ref.current) {
      // TODO Нужно отфильтровать тело от лишних фильтров с relative value, то есть берём первый, а остальные выкидываем (при условии, что есть timeIncrement)
      const valueWithTimeWindow = body?.find(
        value => value.fieldType === SEARCH_FIELD_TYPE.RELATIVE_VALUE && value.timeIncrement
      )

      if (!valueWithTimeWindow) {
        updateTimeWindow(null, '', 0)
      }

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const timeWindow = valueWithTimeWindow?.value?.JSONValue || null
      const timeIncrement = valueWithTimeWindow?.timeIncrement || 0 // Измеряется в минутах
      const hasTimeWindow = checkIsValidTimeWindowInterval(timeWindow)
      const timeWindowField = valueWithTimeWindow?.valueName || ''
      const timeUnit = valueWithTimeWindow?.timeUnit || ETimeUnit.HOUR

      if (hasTimeWindow && timeIncrement > 0) {
        // Сохраняем параметры для возможности перезапуска при изменении сортировки
        lastTimeWindowParamsRef.current = { timeWindow, timeWindowField, timeIncrement, timeUnit }
        updateTimeWindow(timeWindow, timeWindowField, timeIncrement * 1000 * 60, timeUnit) // Переводим количество минут из timeIncrement в миллисекунды
      } else {
        lastTimeWindowParamsRef.current = null
        updateTimeWindow(null, '', 0)
      }

      ref.current.handleClickSearchFilter(body, {
        interval: timeWindow as TimeWindowInterval,
        timeUnit,
        field: timeWindowField,
        isFirstIntervalRun: true,
      })

      setSearchFilter?.(body)
    }
  }

  const handleSearchFilter = (
    criterias: CriteriaType[],
    isUpdateFiltersWithEmptyDefaults?: boolean
  ) => {
    if (ref.current && isShowSearchAssistant && !isUpdateFiltersWithEmptyDefaults) {
      const body = transformFiltersToValuesPOST(criterias)

      updateSearchFilter(body)
    }
  }

  const saRef = useRef<SearchAssistantRef>({ clearCriterias: noop })

  const handleClearSearchFilters = () => {
    setEntity(null)
    saRef.current.clearCriterias()
  }

  const handleResetSearchFilter = () => {
    handleSearchFilter([])
  }

  // Обработчик изменения сортировки - перезапускает временное окно с сохраненными параметрами
  const handleSortChange = useCallback(() => {
    if (lastTimeWindowParamsRef.current) {
      const { timeWindow, timeWindowField, timeIncrement, timeUnit } =
        lastTimeWindowParamsRef.current
      // Перезапускаем временное окно с текущего момента
      updateTimeWindow(timeWindow, timeWindowField, timeIncrement * 1000 * 60, timeUnit)
    }
  }, [updateTimeWindow])

  const localStorageContext = getLocalStorageContext()

  const contextSA = undefined
  const loadingContextSA = false

  const defaultSelectedViewId = useMemo(() => contextSA?.defaultViewId, [contextSA])

  useEffect(() => {
    if (isNotEmpty(searchFilterBody)) {
      updateSearchFilter(searchFilterBody)
    }
  }, [searchFilterBody])

  const agregatedEntityCode =
    selectedEntity?.code || (props.entityCode ?? (entity?.code as string) ?? null)
  const agregatedEntityId = selectedEntity?.id || (props.entityId ?? null)

  const viewSelectorElement = isSearchAssistantDialogWindow && (
    <SearchAssistantViewSelector
      contextId={localStorageContext?.id}
      defaultViewIdSA={defaultSelectedViewId}
      entityId={entity?.id || null}
      loadingContextSA={loadingContextSA}
      searchAssistantId={contextSA?.id || null}
      onClear={handleClearSearchFilters}
      onSelectEntity={handleSelectEntity}
    />
  )

  const isEntitySelectedInSA = useMemo(
    () => Boolean(selectedEntity?.id && selectedEntity?.code),
    [selectedEntity]
  )
  const isShowView = isSearchAssistantDialogWindow ? isEntitySelectedInSA : true

  const willViewLoad = Boolean(selectedEntity) && !entity

  const searchAssistantLoading = willViewLoad

  const isShowSearchAssistant = isSearchAssistantDialogWindow
    ? isSearchAssistantDialogWindow
    : Boolean(entity) || (isViewDialogWindow && willViewLoad)

  return (
    <>
      {isShowSearchAssistant && !hideSA && (
        <SearchAssistant
          ref={saRef}
          contextId={localStorageContext?.id}
          dialogId={dialogId}
          entityInternalId={entity?.internalId}
          entityObjectCode={entity?.objectCode}
          hasActualTimeWindow={!isEmpty(actualTimeWindow)}
          loading={searchAssistantLoading}
          searchAssistantId={contextSA?.id}
          visible={isUndefined(searchFilterBody)}
          consumer={
            isSearchAssistantDialogWindow
              ? SearchAssistantConsumer.dialogWindow
              : SearchAssistantConsumer.page
          }
          onReset={handleResetSearchFilter}
          onSearchFilter={handleSearchFilter}
        >
          {viewSelectorElement}
        </SearchAssistant>
      )}
      {isShowView && (
        <ConfiguredEntityInitialDataWrapper
          key={selectedEntity?.id}
          ref={ref}
          dialogId={dialogId}
          entity={entity}
          hasSearchAssistant={isShowSearchAssistant}
          isSearchAssistantDialogWindow={isSearchAssistantDialogWindow}
          minHeight={entityMinHeight}
          path={path}
          title={title}
          type={ENTITY.VIEW}
          windowHeight={windowHeight}
          onOpenForm={handleOpenForm}
          onSetEntity={handleSetEntity}
          onSortChange={handleSortChange}
          {...props}
          entityCode={agregatedEntityCode}
          entityId={agregatedEntityId}
          isViewDialogWindow={true}
        />
      )}
    </>
  )
}
